import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase, signOutApp } from '../../lib/supabase.js';
import WebTopBar from '../components/WebTopBar.js';
import Icon from '../components/Icon.js';
import { DCard, BtnGold, SLabel, SettingsRow, Toggle, FieldInput, FieldTextarea, Badge } from '../components/MarketplaceAtoms.js';
import { useProfile } from '../hooks/useProfile.js';
import { profileLevelLabel } from '../profileConstants.js';
import { buildProfileLocationSummary } from '../../lib/profileLocationPublic.js';
import { SITE_CONFIG } from '../../constants/siteConfig.js';
import { profileGroovesBalance } from '../constants/groovesEconomy.js';
import {
  PAISES_COMUN,
  paisLabelToCode,
  paisCodeToLabel,
  ciudadOptionsForPaisCode,
  localidadOptionsFor,
  optionsWithLegacy,
  CIUDAD_OPTIONS_ES,
} from '../data/profileLocationPicklists.js';
import UbicacionSelector from '../components/UbicacionSelector.jsx';
import { parseNotificationPrefs, notificationPrefsPatch } from '../../lib/notificationPrefs.js';
import {
  normalizeOutboundUrl,
  socialLinksFromProfile,
  validateAndNormalizeSocialLinks,
  normalizedSocialVisibilitySlots,
  labelForSocialPlatform,
  SOCIAL_PLATFORMS,
} from '../../lib/profileSocialLinks.js';
import { moderateText } from '../../lib/moderationService.js';
import { invokeAwardGrooves } from '../../lib/aiScansService.js';
import {
  fetchGroovesStripePacks,
  groovesPackDisplayLabel,
  groovesPackDisplayPrice,
  mapGroovesCheckoutError,
  startGroovesPackCheckout,
} from '../../lib/groovesStripeService.js';

const AVATAR_BUCKET = 'avatars';

function isProfileCompleteForGroovesRow(p) {
  if (!p) return false;
  if (!String(p.username || '').trim() || String(p.username).trim().length < 2) return false;
  if (!String(p.display_name || '').trim()) return false;
  if (!String(p.avatar_url || '').trim()) return false;
  if (!String(p.location || '').trim()) return false;
  if (!String(p.bio || '').trim()) return false;
  if (!p.phone_verified_at) return false;
  return true;
}

async function edgeFunctionErrorMessage(error) {
  if (!error) return 'Error desconocido';
  let msg = error.message || 'Error';
  if (error.context && typeof error.context.json === 'function') {
    try {
      const j = await error.context.json();
      if (j && typeof j.message === 'string') msg = j.message;
      else if (j && typeof j.error === 'string') msg = j.error;
    } catch (_) {}
  }
  return msg;
}

/**
 * Actualiza `profiles` reintentando sin columnas ausentes (migraciones / caché PostgREST).
 * @returns {{ error: unknown, missingCols: Set<string>, savedWithMissingCols: boolean }}
 */
async function updateProfilePatchResilient(userId, patch) {
  const missingCols = new Set();
  let current = { ...patch };
  let up = null;
  for (let attempt = 0; attempt < 6; attempt++) {
    // eslint-disable-next-line no-await-in-loop
    const res = await supabase.from('profiles').update(current).eq('id', userId);
    up = res.error;
    if (!up) break;
    const raw = up.message || '';
    if (up.code === '23505') break;
    if (
      up.code === '42703' ||
      up.code === 'PGRST204' ||
      /column\s+"[^"]+"\s+does not exist/i.test(raw) ||
      /could not find the\s+'[^']+'\s+column/i.test(raw)
    ) {
      const mPg = raw.match(/column\s+"([^"]+)"/i);
      const mRest = raw.match(/could not find the\s+'([^']+)'\s+column/i);
      const col = mPg?.[1] || mRest?.[1];
      if (col && Object.prototype.hasOwnProperty.call(current, col)) {
        missingCols.add(col);
        const next = { ...current };
        delete next[col];
        current = next;
        continue;
      }
    }
    break;
  }
  const savedWithMissingCols = missingCols.size > 0 && !up;
  return { error: up, missingCols, savedWithMissingCols };
}

const selUbicacionBase = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '0.5px solid rgba(255,255,255,0.12)',
  background: 'rgba(255,255,255,0.06)',
  color: '#fff',
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  cursor: 'pointer',
};

export default function PageAjustes({ onBack, onNavigate, user, initialSub, onInitialSubConsumed }) {
  const { profile, loading: profileLoading, refetch } = useProfile(user?.id);
  const [sub, setSub] = useState(null);
  const [privacidadSaving, setPrivacidadSaving] = useState(false);
  const [privacidadMsg, setPrivacidadMsg] = useState('');
  const [privacidadErr, setPrivacidadErr] = useState('');
  const [notifToggles, setNotifToggles] = useState(() => parseNotificationPrefs(null));
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [notifErr, setNotifErr] = useState('');
  const notifSaveTimerRef = useRef(null);

  const [cuentaForm, setCuentaForm] = useState({
    username: '',
    display_name: '',
    pais: '',
    ciudad: '',
    localidad: '',
    public_show_pais: true,
    public_show_ciudad: true,
    public_show_localidad: false,
    public_show_coleccion: true,
    public_show_joyas: true,
    public_show_ofertas: true,
    public_show_phone: false,
    public_social_visible: [true, true, true],
    bio: '',
    avatar_url: '',
    phone: '',
    website_url: '',
    social_links: [],
  });
  const [cuentaSaving, setCuentaSaving] = useState(false);
  const [publicInfoSaving, setPublicInfoSaving] = useState(false);
  const [publicInfoErr, setPublicInfoErr] = useState('');
  const [publicInfoMsg, setPublicInfoMsg] = useState('');
  const [cuentaMsg, setCuentaMsg] = useState('');
  const [cuentaErr, setCuentaErr] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [groovePacks, setGroovePacks] = useState([]);
  const [grooveShopMsg, setGrooveShopMsg] = useState('');
  const [grooveShopErr, setGrooveShopErr] = useState('');
  const [groovePackBusy, setGroovePackBusy] = useState(null);
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [emailNew, setEmailNew] = useState('');
  const [emailChangeBusy, setEmailChangeBusy] = useState(false);
  const [emailChangeErr, setEmailChangeErr] = useState('');
  const [emailChangeMsg, setEmailChangeMsg] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpBusy, setPhoneOtpBusy] = useState(false);
  const [phoneVerifyBusy, setPhoneVerifyBusy] = useState(false);
  const [phoneRemoveBusy, setPhoneRemoveBusy] = useState(false);
  const [phoneFlowMsg, setPhoneFlowMsg] = useState('');
  const [phoneFlowErr, setPhoneFlowErr] = useState('');
  const cuentaHydrated = useRef(false);
  const scrollEmailSectionRef = useRef(false);
  const fileInputRef = useRef(null);
  const phoneVerified = !!profile?.phone_verified_at && !!String(profile?.phone ?? '').trim();
  const persistNotifPrefs = useCallback(
    async nextToggles => {
      if (!user?.id) return;
      setNotifSaving(true);
      setNotifErr('');
      setNotifMsg('');
      const { error, missingCols } = await updateProfilePatchResilient(user.id, {
        notification_prefs: notificationPrefsPatch(nextToggles),
      });
      setNotifSaving(false);
      if (error) {
        setNotifErr(error.message || 'No se pudieron guardar las preferencias.');
        return;
      }
      if (missingCols?.has('notification_prefs')) {
        setNotifErr('Falta aplicar la migración de preferencias de notificación en Supabase.');
        return;
      }
      setNotifMsg('Preferencias guardadas.');
      refetch();
    },
    [user?.id, refetch]
  );

  const toggleNotif = k => {
    setNotifToggles(p => {
      const next = { ...p, [k]: !p[k] };
      if (user?.id) {
        if (notifSaveTimerRef.current) clearTimeout(notifSaveTimerRef.current);
        notifSaveTimerRef.current = setTimeout(() => persistNotifPrefs(next), 450);
      }
      return next;
    });
  };

  useEffect(() => {
    if (!profile?.id) return;
    setNotifToggles(parseNotificationPrefs(profile.notification_prefs));
  }, [profile?.id, profile?.notification_prefs]);

  const onTopBarSignedOut = async () => {
    onBack();
  };

  const signOutFromAccountPanel = async () => {
    const { error } = await signOutApp();
    if (error) console.warn('Cerrar sesión:', error.message);
    onBack();
  };

  /** Limpia token Supabase en este dispositivo y recarga (alternativa a borrar todo localStorage en F12). */
  const resetLocalSession = async () => {
    const { error } = await signOutApp();
    if (error) console.warn('Restablecer sesión:', error.message);
    if (typeof window !== 'undefined') window.location.reload();
  };

  useEffect(() => {
    if (!initialSub) return;
    const ok = ['cuenta', 'privacidad', 'pagos', 'notif', 'legal', 'idioma'].includes(initialSub);
    if (ok) setSub(initialSub);
    onInitialSubConsumed?.();
  }, [initialSub, onInitialSubConsumed]);

  useEffect(() => {
    if (sub !== 'cuenta' && sub !== 'privacidad') {
      cuentaHydrated.current = false;
      return;
    }
    if (!profile || cuentaHydrated.current) return;
    let pais = profile.pais ?? '';
    let ciudad = profile.ciudad ?? '';
    const localidad = profile.localidad ?? '';
    const locLegacy = typeof profile.location === 'string' ? profile.location.trim() : '';
    if (!String(ciudad).trim() && locLegacy) ciudad = locLegacy;
    if (!String(pais).trim() && ciudad && CIUDAD_OPTIONS_ES.includes(ciudad)) {
      pais = 'España';
    }
    const showPais = typeof profile.public_show_pais === 'boolean' ? profile.public_show_pais : true;
    const showCiudad = typeof profile.public_show_ciudad === 'boolean' ? profile.public_show_ciudad : true;
    const showLoc = typeof profile.public_show_localidad === 'boolean' ? profile.public_show_localidad : false;
    const showColeccion = typeof profile.public_show_coleccion === 'boolean' ? profile.public_show_coleccion : true;
    const showJoyas = typeof profile.public_show_joyas === 'boolean' ? profile.public_show_joyas : true;
    const showOfertas = typeof profile.public_show_ofertas === 'boolean' ? profile.public_show_ofertas : true;
    const showPhone = typeof profile.public_show_phone === 'boolean' ? profile.public_show_phone : false;
    setCuentaForm({
      username: profile.username ?? '',
      display_name: profile.display_name ?? '',
      pais,
      ciudad,
      localidad,
      public_show_pais: showPais,
      public_show_ciudad: showCiudad,
      public_show_localidad: showLoc,
      public_show_coleccion: showColeccion,
      public_show_joyas: showJoyas,
      public_show_ofertas: showOfertas,
      public_show_phone: showPhone,
      public_social_visible: normalizedSocialVisibilitySlots(profile.public_social_visible),
      bio: profile.bio ?? '',
      avatar_url: profile.avatar_url ?? '',
      phone: profile.phone ?? '',
      website_url: profile.website_url ?? '',
      social_links: socialLinksFromProfile(profile),
    });
    cuentaHydrated.current = true;
  }, [sub, profile]);

  useEffect(() => {
    if (sub !== 'cuenta') {
      setEmailNew('');
      setEmailChangeErr('');
      setEmailChangeMsg('');
      return;
    }
    if (!scrollEmailSectionRef.current) return;
    scrollEmailSectionRef.current = false;
    const t = window.setTimeout(() => {
      document.getElementById('ajustes-email-change')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 160);
    return () => window.clearTimeout(t);
  }, [sub]);

  useEffect(() => {
    if (sub !== 'cuenta' || !user?.id) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await fetchGroovesStripePacks();
      if (cancelled) return;
      if (error) setGroovePacks([]);
      else setGroovePacks(Array.isArray(data) ? data : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [sub, user?.id]);

  const handleBuyGroovesPack = async code => {
    setGrooveShopMsg('');
    setGrooveShopErr('');
    setGroovePackBusy(code);
    try {
      const result = await startGroovesPackCheckout(code);
      if (!result.ok) {
        setGrooveShopErr(result.error || mapGroovesCheckoutError('invoke_failed'));
        setGroovePackBusy(null);
      }
    } catch (e) {
      setGrooveShopErr(mapGroovesCheckoutError(e?.message || 'Error al iniciar el pago.'));
      setGroovePackBusy(null);
    }
  };

  const handleAvatarFile = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.id) return;
    setCuentaErr('');
    setAvatarUploading(true);
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
    const path = `${user.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, {
      upsert: true,
      contentType: file.type || 'image/jpeg',
    });
    setAvatarUploading(false);
    if (upErr) {
      setCuentaErr(
        upErr.message?.includes('Bucket not found') || upErr.message?.includes('not found')
          ? 'Falta el bucket «avatars» en Supabase. Aplica la migración 20260422120000_storage_avatars.sql.'
          : upErr.message
      );
      return;
    }
    const { data: pub } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
    const url = pub?.publicUrl;
    if (!url) return;
    setCuentaForm(f => ({ ...f, avatar_url: url }));
    const { error: dbErr } = await supabase.from('profiles').update({ avatar_url: url }).eq('id', user.id);
    if (dbErr) setCuentaErr(dbErr.message);
    else await refetch();
  };

  const handleSavePublicInfo = async () => {
    if (!user?.id) return;
    setPublicInfoErr('');
    setPublicInfoMsg('');
    setCuentaErr('');
    setCuentaMsg('');
    const u = cuentaForm.username.trim();
    if (u.length < 2) {
      setPublicInfoErr('El nombre de usuario debe tener al menos 2 caracteres.');
      return;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(u)) {
      setPublicInfoErr('Usuario solo puede usar letras, números, punto, guion y guion bajo.');
      return;
    }
    const mu = moderateText(u, 'username');
    if (!mu.approved) {
      setPublicInfoErr(`No se pudo guardar · ${mu.reason || 'Revisa el texto.'}`);
      return;
    }
    const mb = moderateText(cuentaForm.bio, 'bio');
    if (!mb.approved) {
      setPublicInfoErr(`No se pudo guardar · ${mb.reason || 'Revisa el texto.'}`);
      return;
    }
    const paisCodigoForUi = paisLabelToCode(cuentaForm.pais);
    if (paisCodigoForUi === 'ES') {
      if (!String(cuentaForm.ciudad || '').trim()) {
        setPublicInfoErr('Revisa ubicación: elige una provincia.');
        return;
      }
    }

    setPublicInfoSaving(true);
    const paisT = cuentaForm.pais.trim() || null;
    const ciudadT = cuentaForm.ciudad.trim() || null;
    const localidadT = cuentaForm.localidad.trim() || null;
    const locationSummary = buildProfileLocationSummary({
      pais: paisT || '',
      ciudad: ciudadT || '',
      localidad: localidadT || '',
    });
    const publicPatch = {
      username: u,
      display_name: cuentaForm.display_name.trim() || null,
      pais: paisT,
      ciudad: ciudadT,
      localidad: localidadT,
      public_show_pais: !!cuentaForm.public_show_pais,
      public_show_ciudad: !!cuentaForm.public_show_ciudad,
      public_show_localidad: !!cuentaForm.public_show_localidad,
      public_show_coleccion: !!cuentaForm.public_show_coleccion,
      public_show_joyas: !!cuentaForm.public_show_joyas,
      public_show_ofertas: !!cuentaForm.public_show_ofertas,
      public_show_phone: !!cuentaForm.public_show_phone && phoneVerified,
      public_social_visible: normalizedSocialVisibilitySlots(cuentaForm.public_social_visible),
      location: locationSummary,
      bio: cuentaForm.bio.trim() || null,
    };

    const { error: up, missingCols, savedWithMissingCols } = await updateProfilePatchResilient(user.id, publicPatch);

    if (up) {
      setPublicInfoSaving(false);
      const raw = up.message || '';
      if (up.code === '23505') {
        setPublicInfoErr('Ese nombre de usuario ya está en uso.');
      } else if (missingCols.size > 0 || up.code === '42703' || raw.includes('does not exist')) {
        const affectsUbicacion = [...missingCols].some(k =>
          ['pais', 'ciudad', 'localidad', 'public_show_pais', 'public_show_ciudad', 'public_show_localidad', 'location'].includes(k)
        );
        const affectsPriv = [...missingCols].some(k => k.startsWith('public_show_') || k === 'public_social_visible');
        if (affectsUbicacion) setPublicInfoErr('No se pudo guardar la ubicación. Revisa país y provincia y vuelve a intentar.');
        else if (affectsPriv) setPublicInfoErr('No se pudieron guardar algunas opciones de visibilidad. Inténtalo de nuevo.');
        else setPublicInfoErr('No se pudieron guardar todos los cambios. Inténtalo de nuevo más tarde.');
      } else if (/row level security/i.test(raw) || /permission denied/i.test(raw)) {
        setPublicInfoErr('No tienes permisos para guardar estos cambios. Cierra sesión e inicia de nuevo.');
      } else if (/invalid input syntax for type/i.test(raw) || /value too long/i.test(raw)) {
        setPublicInfoErr('Revisa los campos: hay un valor con formato no válido o demasiado largo.');
      } else {
        setPublicInfoErr('No se pudieron guardar los cambios. Revisa los campos y vuelve a intentar.');
      }
      return;
    }

    await supabase.auth.updateUser({
      data: {
        username: u,
        full_name: cuentaForm.display_name.trim() || undefined,
      },
    });
    const { data: freshProfile } = await supabase
      .from('profiles')
      .select('username, display_name, avatar_url, location, bio, phone_verified_at')
      .eq('id', user.id)
      .maybeSingle();
    if (isProfileCompleteForGroovesRow(freshProfile)) {
      try {
        await invokeAwardGrooves({
          reason: 'perfil_completo',
          refId: user.id,
          refType: 'profile',
        });
      } catch (e) {
        console.warn('[PageAjustes] perfil_completo grooves (info pública):', e);
      }
    }
    setPublicInfoSaving(false);
    if (savedWithMissingCols) {
      const affectsUbicacion = [...missingCols].some(k =>
        ['pais', 'ciudad', 'localidad', 'public_show_pais', 'public_show_ciudad', 'public_show_localidad', 'location'].includes(k)
      );
      setPublicInfoMsg(affectsUbicacion ? 'Guardado parcial: la ubicación no se pudo actualizar.' : 'Guardado parcial: algunos campos no se pudieron actualizar.');
    } else {
      setPublicInfoMsg('Información pública guardada.');
    }
    await refetch();
  };

  const handleSaveCuenta = async () => {
    if (!user?.id) return;
    setCuentaErr('');
    setCuentaMsg('');
    setPublicInfoErr('');
    setPublicInfoMsg('');
    const u = cuentaForm.username.trim();
    if (u.length < 2) {
      setCuentaErr('El nombre de usuario debe tener al menos 2 caracteres.');
      return;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(u)) {
      setCuentaErr('Usuario solo puede usar letras, números, punto, guion y guion bajo.');
      return;
    }
    const mu = moderateText(u, 'username');
    if (!mu.approved) {
      setCuentaErr(`No se pudo guardar · ${mu.reason || 'Revisa el texto.'}`);
      return;
    }
    const mb = moderateText(cuentaForm.bio, 'bio');
    if (!mb.approved) {
      setCuentaErr(`No se pudo guardar · ${mb.reason || 'Revisa el texto.'}`);
      return;
    }

    // Ayuda al usuario: validación mínima de ubicación (no depender del error SQL).
    const paisCodigoForUi = paisLabelToCode(cuentaForm.pais);
    if (paisCodigoForUi === 'ES') {
      if (!String(cuentaForm.ciudad || '').trim()) {
        setCuentaErr('Revisa ubicación: elige una provincia.');
        return;
      }
      // Municipio (localidad) es opcional.
    }

    const socialRes = validateAndNormalizeSocialLinks(cuentaForm.social_links);
    if (!socialRes.ok) {
      setCuentaErr(`Revisa redes sociales: ${socialRes.message}`);
      return;
    }
    setCuentaSaving(true);
    const paisT = cuentaForm.pais.trim() || null;
    const ciudadT = cuentaForm.ciudad.trim() || null;
    const localidadT = cuentaForm.localidad.trim() || null;
    const locationSummary = buildProfileLocationSummary({
      pais: paisT || '',
      ciudad: ciudadT || '',
      localidad: localidadT || '',
    });
    const fullPatch = {
      username: u,
      display_name: cuentaForm.display_name.trim() || null,
      pais: paisT,
      ciudad: ciudadT,
      localidad: localidadT,
      public_show_pais: !!cuentaForm.public_show_pais,
      public_show_ciudad: !!cuentaForm.public_show_ciudad,
      public_show_localidad: !!cuentaForm.public_show_localidad,
      public_show_coleccion: !!cuentaForm.public_show_coleccion,
      public_show_joyas: !!cuentaForm.public_show_joyas,
      public_show_ofertas: !!cuentaForm.public_show_ofertas,
      public_show_phone: !!cuentaForm.public_show_phone && phoneVerified,
      public_social_visible: normalizedSocialVisibilitySlots(cuentaForm.public_social_visible),
      location: locationSummary,
      bio: cuentaForm.bio.trim() || null,
      avatar_url: cuentaForm.avatar_url.trim() || null,
      website_url: normalizeOutboundUrl(cuentaForm.website_url),
      social_links: socialRes.value,
      instagram_url: null,
      discogs_url: null,
      twitter_url: null,
      other_social_url: null,
    };

    const { error: up, missingCols, savedWithMissingCols } = await updateProfilePatchResilient(user.id, fullPatch);

    if (up) {
      setCuentaSaving(false);
      const raw = up.message || '';
      console.warn('[PageAjustes] save failed:', { code: up.code, message: up.message, details: up.details, hint: up.hint });
      if (up.code === '23505') {
        setCuentaErr('Ese nombre de usuario ya está en uso.');
      } else if (missingCols.size > 0 || up.code === '42703' || raw.includes('does not exist')) {
        console.warn('[PageAjustes] Guardado parcial falló por columnas ausentes:', {
          missing: [...missingCols],
          error: { code: up.code, message: up.message, details: up.details, hint: up.hint },
          attemptedPatchKeys: Object.keys(fullPatch),
        });
        // Mensaje corto, pero con pistas accionables para el usuario
        const affectsUbicacion = [...missingCols].some(k => ['pais', 'ciudad', 'localidad', 'public_show_pais', 'public_show_ciudad', 'public_show_localidad'].includes(k));
        const affectsRedes = [...missingCols].includes('social_links') || [...missingCols].includes('website_url');
        const affectsPriv = [...missingCols].some(k => k.startsWith('public_show_') || k === 'public_social_visible');
        if (affectsUbicacion) setCuentaErr('No se pudo guardar la ubicación. Revisa país y provincia y vuelve a intentar.');
        else if (affectsRedes) setCuentaErr('No se pudieron guardar los enlaces de contacto/redes. Revisa el formato e inténtalo de nuevo.');
        else if (affectsPriv) setCuentaErr('No se pudieron guardar algunas opciones de visibilidad. Inténtalo de nuevo.');
        else setCuentaErr('No se pudieron guardar todos los cambios. Inténtalo de nuevo más tarde.');
      } else {
        // Mensajes útiles por tipo de fallo
        if (/row level security/i.test(raw) || /permission denied/i.test(raw)) {
          setCuentaErr('No tienes permisos para guardar estos cambios. Cierra sesión e inicia de nuevo.');
        } else if (/invalid input syntax for type/i.test(raw) || /value too long/i.test(raw)) {
          setCuentaErr('Revisa los campos: hay un valor con formato no válido o demasiado largo.');
        } else {
          setCuentaErr('No se pudieron guardar los cambios. Revisa los campos y vuelve a intentar.');
        }
      }
      return;
    }
    if (pwNew || pwConfirm) {
      if (pwNew.length < 6) {
        setCuentaSaving(false);
        setCuentaErr('La nueva contraseña debe tener al menos 6 caracteres.');
        return;
      }
      if (pwNew !== pwConfirm) {
        setCuentaSaving(false);
        setCuentaErr('Las contraseñas nuevas no coinciden.');
        return;
      }
      const { error: pwErr } = await supabase.auth.updateUser({ password: pwNew });
      if (pwErr) {
        setCuentaSaving(false);
        setCuentaErr(pwErr.message);
        return;
      }
      setPwNew('');
      setPwConfirm('');
    }
    await supabase.auth.updateUser({
      data: {
        username: u,
        full_name: cuentaForm.display_name.trim() || undefined,
      },
    });
    const { data: freshProfile } = await supabase
      .from('profiles')
      .select('username, display_name, avatar_url, location, bio, phone_verified_at')
      .eq('id', user.id)
      .maybeSingle();
    if (isProfileCompleteForGroovesRow(freshProfile)) {
      try {
        await invokeAwardGrooves({
          reason: 'perfil_completo',
          refId: user.id,
          refType: 'profile',
        });
      } catch (e) {
        console.warn('[PageAjustes] perfil_completo grooves:', e);
      }
    }
    setCuentaSaving(false);
    if (savedWithMissingCols) {
      const affectsUbicacion = [...missingCols].some(k => ['pais', 'ciudad', 'localidad', 'public_show_pais', 'public_show_ciudad', 'public_show_localidad'].includes(k));
      setCuentaMsg(affectsUbicacion ? 'Guardado parcial: la ubicación no se pudo actualizar.' : 'Guardado parcial: algunos campos no se pudieron actualizar.');
      console.warn('[PageAjustes] Guardado parcial por columnas ausentes (éxito con reintentos):', [...missingCols]);
    } else {
      setCuentaMsg('Cambios guardados.');
    }
    await refetch();
  };

  const handleSendPhoneOtp = useCallback(async () => {
    if (!user?.id) return;
    setPhoneFlowErr('');
    setPhoneFlowMsg('');
    const raw = cuentaForm.phone.trim();
    if (!raw) {
      setPhoneFlowErr('Escribe el número al que debemos enviar el SMS.');
      return;
    }
    if (raw.replace(/[\s\-+().]/g, '').length < 5) {
      setPhoneFlowErr('Número demasiado corto. Usa formato internacional (ej. +34 612 345 678).');
      return;
    }
    setPhoneOtpBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('phone-send-otp', { body: { phone: raw } });
      if (error) {
        const msg = await edgeFunctionErrorMessage(error);
        setPhoneFlowErr(
          msg?.includes('Failed to fetch') || msg?.includes('Network') || msg?.includes('fetch')
            ? 'No se pudo enviar el código ahora mismo. Revisa tu conexión e inténtalo de nuevo en unos minutos.'
            : msg
        );
        return;
      }
      if (data && typeof data === 'object' && data.error) {
        setPhoneFlowErr(data.message || String(data.error));
        return;
      }
      setPhoneFlowMsg('Te hemos enviado un SMS con un código de 6 dígitos (válido 10 minutos).');
      setPhoneOtp('');
    } finally {
      setPhoneOtpBusy(false);
    }
  }, [user?.id, cuentaForm.phone]);

  const handleVerifyPhoneOtp = useCallback(async () => {
    if (!user?.id) return;
    setPhoneFlowErr('');
    setPhoneFlowMsg('');
    const raw = cuentaForm.phone.trim();
    const code = phoneOtp.replace(/\D/g, '');
    if (!raw || code.length !== 6) {
      setPhoneFlowErr('Introduce el mismo número y el código de 6 dígitos del SMS.');
      return;
    }
    setPhoneVerifyBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('phone-verify-otp', { body: { phone: raw, code } });
      if (error) {
        const msg = await edgeFunctionErrorMessage(error);
        setPhoneFlowErr(
          msg?.includes('Failed to fetch') || msg?.includes('Network') || msg?.includes('fetch')
            ? 'No se pudo verificar el código ahora mismo. Revisa tu conexión e inténtalo de nuevo.'
            : msg
        );
        return;
      }
      if (data && typeof data === 'object' && data.error) {
        setPhoneFlowErr(data.message || String(data.error));
        return;
      }
      setPhoneFlowMsg('Teléfono verificado correctamente.');
      setPhoneOtp('');
      cuentaHydrated.current = false;
      await refetch();
    } finally {
      setPhoneVerifyBusy(false);
    }
  }, [user?.id, cuentaForm.phone, phoneOtp, refetch]);

  const handleRemoveVerifiedPhone = useCallback(async () => {
    if (!user?.id) return;
    if (!window.confirm('¿Quitar el teléfono verificado de tu cuenta?')) return;
    setPhoneRemoveBusy(true);
    setPhoneFlowErr('');
    setPhoneFlowMsg('');
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ phone: null, phone_verified_at: null, public_show_phone: false })
        .eq('id', user.id);
      if (error) {
        setPhoneFlowErr(error.message);
        return;
      }
      setCuentaForm(f => ({ ...f, phone: '', public_show_phone: false }));
      setPhoneFlowMsg('Teléfono eliminado. Puedes añadir otro cuando quieras.');
      cuentaHydrated.current = false;
      await refetch();
    } finally {
      setPhoneRemoveBusy(false);
    }
  }, [user?.id, refetch]);

  const handleSavePrivacidad = async () => {
    if (!user?.id) return;
    if (!profile || profileLoading) {
      setPrivacidadErr('Espera a que cargue el perfil antes de guardar.');
      return;
    }
    setPrivacidadErr('');
    setPrivacidadMsg('');
    setPrivacidadSaving(true);
    try {
      const patch = {
        public_show_pais: !!cuentaForm.public_show_pais,
        public_show_ciudad: !!cuentaForm.public_show_ciudad,
        public_show_localidad: !!cuentaForm.public_show_localidad,
        public_show_coleccion: !!cuentaForm.public_show_coleccion,
        public_show_joyas: !!cuentaForm.public_show_joyas,
        public_show_ofertas: !!cuentaForm.public_show_ofertas,
        public_show_phone: !!cuentaForm.public_show_phone && phoneVerified,
        public_social_visible: normalizedSocialVisibilitySlots(cuentaForm.public_social_visible),
      };
      const { error: up } = await updateProfilePatchResilient(user.id, patch);
      if (up) {
        const raw = up.message || '';
        setPrivacidadErr(
          /row level security/i.test(raw) || /permission denied/i.test(raw)
            ? 'No tienes permisos. Cierra sesión e inicia de nuevo.'
            : raw || 'No se pudo guardar la visibilidad.'
        );
        return;
      }
      setPrivacidadMsg('Visibilidad guardada.');
      cuentaHydrated.current = false;
      await refetch();
    } finally {
      setPrivacidadSaving(false);
    }
  };

  const displayName = profile?.display_name?.trim() || profile?.username || user?.user_metadata?.username || user?.email?.split('@')[0] || 'Usuario';
  const emailVerified = !!user?.email_confirmed_at;

  const openSupportEmail = () => {
    const to = SITE_CONFIG?.emailSoporte || 'soporte@dreamvinyl.es';
    const subject = encodeURIComponent('Soporte DreamVinyl');
    const body = encodeURIComponent(
      [
        'Hola equipo DreamVinyl,',
        '',
        'Necesito ayuda con:',
        '',
        '---',
        `Usuario: ${user?.email || user?.id || '—'}`,
        `Fecha: ${new Date().toLocaleString('es-ES')}`,
      ].join('\n')
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const handleRequestEmailChange = async () => {
    if (!user?.id) return;
    setEmailChangeErr('');
    setEmailChangeMsg('');
    const trimmed = emailNew.trim().toLowerCase();
    if (!trimmed) {
      setEmailChangeErr('Escribe el nuevo correo.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailChangeErr('Introduce un correo con formato válido.');
      return;
    }
    if (trimmed === String(user?.email || '').trim().toLowerCase()) {
      setEmailChangeErr('Ese correo es el que usas ahora.');
      return;
    }
    setEmailChangeBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ email: trimmed });
      if (error) {
        const em = (error.message || '').toLowerCase();
        let msg = error.message || 'No se pudo solicitar el cambio.';
        if (em.includes('rate') || em.includes('seconds')) {
          msg = 'Demasiados intentos. Espera un minuto y prueba de nuevo.';
        } else if (em.includes('already been registered') || em.includes('already registered')) {
          msg = 'Ese correo ya está registrado en otra cuenta.';
        }
        setEmailChangeErr(msg);
        return;
      }
      setEmailNew('');
      setEmailChangeMsg(
        'Te hemos enviado un enlace al nuevo correo para confirmar el cambio. Revisa también spam. Hasta que lo confirmes, inicia sesión con el correo actual.'
      );
    } finally {
      setEmailChangeBusy(false);
    }
  };

  const topBar = (title, back) => (
    <WebTopBar
      onBack={back}
      title={title}
      mini
      dark
      user={user}
      onNavigate={onNavigate}
      onSignOut={onTopBarSignedOut}
    />
  );

  if (sub === 'cuenta') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Editar perfil', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          {!user && (
            <DCard style={{ padding: 16, marginBottom: 14 }}>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: 0 }}>Inicia sesión para editar tu perfil.</p>
            </DCard>
          )}
          {user && (
            <>
              <SLabel>Foto de perfil</SLabel>
              <DCard style={{ padding: 16, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div
                    style={{
                      width: 88,
                      height: 88,
                      borderRadius: '50%',
                      border: `2px solid ${C.gold}`,
                      overflow: 'hidden',
                      background: '#1a1a2e',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {cuentaForm.avatar_url ? (
                      <img src={cuentaForm.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 28, fontWeight: 600, color: C.gold }}>
                        {(cuentaForm.display_name || cuentaForm.username || user.email || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} onChange={handleAvatarFile} />
                    <button
                      type="button"
                      disabled={avatarUploading || profileLoading}
                      onClick={() => fileInputRef.current?.click()}
                      style={{
                        padding: '10px 16px',
                        borderRadius: 10,
                        border: '0.5px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.06)',
                        color: '#fff',
                        fontSize: 12,
                        cursor: avatarUploading ? 'wait' : 'pointer',
                        marginBottom: 8,
                      }}>
                      {avatarUploading ? 'Subiendo…' : 'Elegir imagen'}
                    </button>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
                      JPG, PNG, WebP o GIF. Máx. recomendado 5 MB. La imagen se guarda en tu carpeta de Supabase Storage.
                    </div>
                  </div>
                </div>
              </DCard>

              <SLabel>Información pública</SLabel>
              <DCard style={{ padding: 16, marginBottom: 14 }}>
                <FieldInput
                  label="Nombre de usuario (único)"
                  value={cuentaForm.username}
                  onChange={e => setCuentaForm(f => ({ ...f, username: e.target.value }))}
                  autoComplete="username"
                />
                <FieldInput
                  label="Nombre visible"
                  value={cuentaForm.display_name}
                  onChange={e => setCuentaForm(f => ({ ...f, display_name: e.target.value }))}
                  placeholder="Cómo quieres que te vean otros melómanos"
                />
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', margin: '0 0 10px', lineHeight: 1.45 }}>
                  <strong style={{ color: 'rgba(255,255,255,0.65)' }}>Ubicación.</strong> Elige tu zona y marca «Mostrar» en cada nivel para decidir qué verá la gente en tu perfil. Esta ubicación se usa para <strong style={{ color: 'rgba(255,255,255,0.65)' }}>buscar discos cerca</strong> y para ayudarte al publicar (puedes usar «Del perfil»).
                </p>
                {(() => {
                  const paisCodigo = paisLabelToCode(cuentaForm.pais);
                  const rawCiudadOpts = ciudadOptionsForPaisCode(paisCodigo);
                  const ciudadOpts = paisCodigo ? optionsWithLegacy(rawCiudadOpts, cuentaForm.ciudad) : [];
                  const ciudadOk = !!String(cuentaForm.ciudad || '').trim();
                  const rawLocOpts = paisCodigo && ciudadOk ? localidadOptionsFor(paisCodigo, cuentaForm.ciudad) : [];
                  const locOpts = paisCodigo && ciudadOk ? optionsWithLegacy(rawLocOpts, cuentaForm.localidad) : [];
                  const rowShow = (label, showKey, children) => (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{label}</span>
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 10,
                            color: 'rgba(255,255,255,0.5)',
                            cursor: 'pointer',
                            flexShrink: 0,
                            userSelect: 'none',
                          }}>
                          <input
                            type="checkbox"
                            checked={!!cuentaForm[showKey]}
                            onChange={e => setCuentaForm(f => ({ ...f, [showKey]: e.target.checked }))}
                            style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer' }}
                          />
                          Mostrar
                        </label>
                      </div>
                      {children}
                    </div>
                  );
                  return (
                    <>
                      {rowShow(
                        'País',
                        'public_show_pais',
                        <select
                          value={paisCodigo}
                          onChange={e => {
                            const code = e.target.value;
                            const label = paisCodeToLabel(code);
                            setCuentaForm(f => ({
                              ...f,
                              pais: label,
                              ciudad: '',
                              localidad: '',
                            }));
                          }}
                          style={selUbicacionBase}
                          onFocus={e => {
                            e.target.style.borderColor = C.gold;
                          }}
                          onBlur={e => {
                            e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                          }}>
                          <option value="" style={{ background: '#1a1d28', color: '#fff' }}>
                            — Elige país —
                          </option>
                          {PAISES_COMUN.map(p => (
                            <option key={p.v} value={p.v} style={{ background: '#1a1d28', color: '#fff' }}>
                              {p.l}
                            </option>
                          ))}
                        </select>
                      )}
                      {paisCodigo === 'ES' ? (
                        <>
                          <div style={{ marginBottom: 12 }}>
                            <UbicacionSelector
                              variant="dark"
                              soloEspana
                              provincia={cuentaForm.ciudad}
                              municipio={cuentaForm.localidad}
                              onProvinciaChange={n => setCuentaForm(f => ({ ...f, ciudad: n, localidad: '' }))}
                              onMunicipioChange={n => setCuentaForm(f => ({ ...f, localidad: n }))}
                            />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Mostrar provincia / isla en el perfil público</span>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={!!cuentaForm.public_show_ciudad}
                                  onChange={e => setCuentaForm(f => ({ ...f, public_show_ciudad: e.target.checked }))}
                                  style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer' }}
                                />
                                Mostrar
                              </label>
                            </div>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Mostrar municipio en el perfil público</span>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={!!cuentaForm.public_show_localidad}
                                  onChange={e => setCuentaForm(f => ({ ...f, public_show_localidad: e.target.checked }))}
                                  style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer' }}
                                />
                                Mostrar
                              </label>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {rowShow(
                            'Ciudad o zona',
                            'public_show_ciudad',
                            <select
                              value={ciudadOpts.includes(cuentaForm.ciudad) ? cuentaForm.ciudad : ''}
                              disabled={!paisCodigo}
                              onChange={e => {
                                const next = e.target.value;
                                setCuentaForm(f => {
                                  const pc = paisLabelToCode(f.pais);
                                  const opts = localidadOptionsFor(pc, next);
                                  const nextLoc = opts.includes(f.localidad) ? f.localidad : opts[0] ?? '';
                                  return { ...f, ciudad: next, localidad: nextLoc };
                                });
                              }}
                              style={{ ...selUbicacionBase, opacity: !paisCodigo ? 0.45 : 1 }}
                              onFocus={e => {
                                e.target.style.borderColor = C.gold;
                              }}
                              onBlur={e => {
                                e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                              }}>
                              <option value="" style={{ background: '#1a1d28', color: '#fff' }}>
                                {!paisCodigo ? '— Primero elige país —' : '— Elige opción —'}
                              </option>
                              {ciudadOpts.map(c => (
                                <option key={c} value={c} style={{ background: '#1a1d28', color: '#fff' }}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          )}
                          {rowShow(
                            'Localidad / barrio',
                            'public_show_localidad',
                            <select
                              value={locOpts.includes(cuentaForm.localidad) ? cuentaForm.localidad : ''}
                              disabled={!paisCodigo || !ciudadOk}
                              onChange={e => setCuentaForm(f => ({ ...f, localidad: e.target.value }))}
                              style={{ ...selUbicacionBase, opacity: !paisCodigo || !ciudadOk ? 0.45 : 1 }}
                              onFocus={e => {
                                e.target.style.borderColor = C.gold;
                              }}
                              onBlur={e => {
                                e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                              }}>
                              <option value="" style={{ background: '#1a1d28', color: '#fff' }}>
                                {!paisCodigo || !ciudadOk ? '— Primero elige ciudad —' : '— Elige opción —'}
                              </option>
                              {locOpts.map(c => (
                                <option key={c} value={c} style={{ background: '#1a1d28', color: '#fff' }}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
                <FieldTextarea
                  label="Bio"
                  rows={4}
                  value={cuentaForm.bio}
                  onChange={e => setCuentaForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="Cuéntanos sobre tu colección…"
                />
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 6 }}>Visibilidad en perfil público (vendedor)</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      ['public_show_coleccion', 'Mostrar pestaña Colección'],
                      ['public_show_joyas', 'Mostrar pestaña Joyas'],
                      ['public_show_ofertas', 'Mostrar pestaña Ofertas'],
                    ].map(([key, label]) => (
                      <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 11, color: 'rgba(255,255,255,0.62)' }}>
                        <input
                          type="checkbox"
                          checked={!!cuentaForm[key]}
                          onChange={e => setCuentaForm(f => ({ ...f, [key]: e.target.checked }))}
                          style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer' }}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                      Teléfono y redes en el perfil público (tres huecos = orden de registro en Contacto y redes)
                    </div>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        cursor: phoneVerified ? 'pointer' : 'default',
                        fontSize: 11,
                        color: phoneVerified ? 'rgba(255,255,255,0.62)' : 'rgba(255,255,255,0.4)',
                        marginBottom: 8,
                      }}>
                      <input
                        type="checkbox"
                        disabled={!phoneVerified}
                        checked={!!cuentaForm.public_show_phone}
                        onChange={e => setCuentaForm(f => ({ ...f, public_show_phone: e.target.checked }))}
                        style={{ width: 15, height: 15, accentColor: C.gold, cursor: phoneVerified ? 'pointer' : 'not-allowed', marginTop: 2, flexShrink: 0 }}
                      />
                      <span>
                        Mostrar teléfono verificado en el perfil público
                        {!phoneVerified ? (
                          <span style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                            Verifica tu número en «Contacto y redes» para activar esta opción.
                          </span>
                        ) : null}
                      </span>
                    </label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[0, 1, 2].map(i => {
                        const rows = Array.isArray(cuentaForm.social_links) ? cuentaForm.social_links : [];
                        const row = rows[i];
                        const url = String(row?.url || '').trim();
                        const pl = String(row?.platform || SOCIAL_PLATFORMS[0].id);
                        const name = url ? labelForSocialPlatform(pl) : `Red social ${i + 1} (vacía)`;
                        const vis = normalizedSocialVisibilitySlots(cuentaForm.public_social_visible);
                        return (
                          <label
                            key={`pub-soc-${i}`}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 11, color: 'rgba(255,255,255,0.62)' }}>
                            <input
                              type="checkbox"
                              checked={!!vis[i]}
                              onChange={e => {
                                const on = e.target.checked;
                                setCuentaForm(f => {
                                  const next = normalizedSocialVisibilitySlots(f.public_social_visible);
                                  next[i] = on;
                                  return { ...f, public_social_visible: next };
                                });
                              }}
                              style={{ width: 15, height: 15, accentColor: C.gold, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
                            />
                            <span>
                              Mostrar enlace: <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{name}</strong>
                              {!url ? (
                                <span style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>
                                  Hueco {i + 1} del formulario de redes; el orden coincide con el de arriba abajo en Contacto y redes.
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {publicInfoErr ? (
                  <div
                    style={{
                      marginTop: 12,
                      background: 'rgba(226,75,74,0.1)',
                      border: '0.5px solid rgba(226,75,74,0.35)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      fontSize: 12,
                      color: C.danger,
                    }}>
                    {publicInfoErr}
                  </div>
                ) : null}
                {publicInfoMsg ? (
                  <div
                    style={{
                      marginTop: 12,
                      background: 'rgba(29,158,117,0.1)',
                      border: '0.5px solid rgba(29,158,117,0.35)',
                      borderRadius: 10,
                      padding: '10px 12px',
                      fontSize: 12,
                      color: C.green,
                    }}>
                    {publicInfoMsg}
                  </div>
                ) : null}
                <BtnGold
                  type="button"
                  onClick={handleSavePublicInfo}
                  disabled={publicInfoSaving || cuentaSaving}
                  style={{ marginTop: 14, width: '100%', maxWidth: 280, opacity: publicInfoSaving || cuentaSaving ? 0.7 : 1 }}>
                  {publicInfoSaving ? 'Guardando…' : 'Guardar información pública'}
                </BtnGold>
              </DCard>

              <SLabel>Invita y Grooves</SLabel>
              <DCard style={{ padding: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5, marginBottom: 10 }}>
                  Comparte tu enlace de referido (Grooves extra para ti y tu referido cuando se complete el registro).
                </div>
                {profile?.referral_code ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
                    <code
                      style={{
                        flex: '1 1 220px',
                        fontSize: 11,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'rgba(0,0,0,0.35)',
                        color: '#c8e6c9',
                        wordBreak: 'break-all',
                      }}>
                      {`${SITE_CONFIG.urlPublica}/?ref=${profile.referral_code}`}
                    </code>
                    <BtnGold
                      type="button"
                      style={{ width: 'auto', minWidth: 120 }}
                      onClick={() => {
                        const t = `${SITE_CONFIG.urlPublica}/?ref=${profile.referral_code}`;
                        navigator.clipboard?.writeText(t).catch(() => {});
                        setGrooveShopMsg('Enlace copiado al portapapeles.');
                      }}>
                      Copiar enlace
                    </BtnGold>
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Generando código de referido…</div>
                )}
                <div style={{ fontSize: 12, fontWeight: 600, color: C.gold, marginTop: 14, marginBottom: 8 }}>
                  Comprar Grooves (Stripe)
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 8, lineHeight: 1.45 }}>
                  Pago seguro con Stripe. Tras confirmar el cobro, los Grooves se abonan en tu cuenta en unos segundos.
                </div>
                {grooveShopErr ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: C.danger,
                      marginBottom: 8,
                      lineHeight: 1.45,
                      background: 'rgba(226,75,74,0.08)',
                      padding: '8px 10px',
                      borderRadius: 8,
                    }}>
                    {grooveShopErr}
                  </div>
                ) : null}
                {grooveShopMsg ? (
                  <div style={{ fontSize: 11, color: 'rgba(200,168,75,0.9)', marginBottom: 8 }}>{grooveShopMsg}</div>
                ) : null}
                {!phoneVerified ? (
                  <div style={{ fontSize: 11, color: 'rgba(239,159,39,0.95)', lineHeight: 1.45 }}>
                    Verifica tu teléfono en esta sección antes de comprar Grooves.
                  </div>
                ) : groovePacks.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {groovePacks.map(p => {
                      const busy = groovePackBusy === p.code;
                      return (
                        <div
                          key={p.code}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: 'rgba(255,255,255,0.04)',
                            border: '0.5px solid rgba(200,168,75,0.2)',
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
                              {groovesPackDisplayLabel(p)}
                            </div>
                            <div style={{ fontSize: 11, color: C.gold, marginTop: 4, fontWeight: 600 }}>
                              {groovesPackDisplayPrice(p) || '—'}
                            </div>
                          </div>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleBuyGroovesPack(p.code)}
                            style={{
                              flexShrink: 0,
                              padding: '8px 14px',
                              borderRadius: 8,
                              border: `0.5px solid ${C.gold}`,
                              background: busy ? 'rgba(200,168,75,0.08)' : 'rgba(200,168,75,0.18)',
                              color: C.gold,
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: busy ? 'wait' : 'pointer',
                              opacity: busy ? 0.7 : 1,
                            }}
                          >
                            {busy ? '…' : 'Comprar'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                    No hay paquetes activos con precio Stripe.
                  </div>
                )}
              </DCard>

              <SLabel>Contacto y redes</SLabel>
              <DCard style={{ padding: 16, marginBottom: 14 }}>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', margin: '0 0 12px', lineHeight: 1.45 }}>
                  <strong style={{ color: 'rgba(255,255,255,0.65)' }}>Teléfono:</strong> solo se guarda en tu cuenta tras verificar el código que enviamos por SMS. La web y hasta 3 redes se guardan con «Guardar cambios» y se muestran en tu perfil público.
                </p>
                <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Estado</span>
                    {phoneVerified ? (
                      <Badge type="green">Teléfono verificado</Badge>
                    ) : (
                      <Badge type="warn">Sin verificar</Badge>
                    )}
                  </div>
                  <FieldInput
                    label="Número (mismo que recibirá el SMS)"
                    type="tel"
                    autoComplete="tel"
                    value={cuentaForm.phone}
                    onChange={e => {
                      setPhoneFlowErr('');
                      setPhoneFlowMsg('');
                      setCuentaForm(f => ({ ...f, phone: e.target.value }));
                    }}
                    placeholder="+34 600 000 000"
                  />
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', margin: '6px 0 10px', lineHeight: 1.4 }}>
                    Formato recomendado: <strong style={{ color: 'rgba(255,255,255,0.45)' }}>+34</strong> y el número (espacios o guiones opcionales). Si pones{' '}
                    <strong style={{ color: 'rgba(255,255,255,0.45)' }}>9 cifras</strong> que empiecen por 6, 7, 8 o 9, se interpretan como España (+34). También puedes usar prefijo{' '}
                    <strong style={{ color: 'rgba(255,255,255,0.45)' }}>00</strong> en lugar de +.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    <BtnGold
                      type="button"
                      disabled={phoneOtpBusy || phoneVerifyBusy || !user}
                      onClick={handleSendPhoneOtp}
                      style={{ padding: '8px 14px', fontSize: 11, borderRadius: 8 }}>
                      {phoneOtpBusy ? 'Enviando SMS…' : 'Enviar código SMS'}
                    </BtnGold>
                  </div>
                  <FieldInput
                    label="Código de 6 dígitos"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={phoneOtp}
                    onChange={e => {
                      setPhoneFlowErr('');
                      setPhoneFlowMsg('');
                      setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6));
                    }}
                    placeholder="123456"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    <BtnGold
                      type="button"
                      disabled={phoneVerifyBusy || phoneOtpBusy || !user}
                      onClick={handleVerifyPhoneOtp}
                      style={{ padding: '8px 14px', fontSize: 11, borderRadius: 8 }}>
                      {phoneVerifyBusy ? 'Verificando…' : 'Confirmar código'}
                    </BtnGold>
                    {phoneVerified ? (
                      <button
                        type="button"
                        disabled={phoneRemoveBusy || phoneOtpBusy || phoneVerifyBusy}
                        onClick={handleRemoveVerifiedPhone}
                        style={{
                          padding: '8px 14px',
                          fontSize: 11,
                          borderRadius: 8,
                          color: 'rgba(255,255,255,0.65)',
                          background: 'transparent',
                          border: '0.5px solid rgba(255,255,255,0.15)',
                          cursor: phoneRemoveBusy || phoneOtpBusy || phoneVerifyBusy ? 'wait' : 'pointer',
                          opacity: phoneRemoveBusy || phoneOtpBusy || phoneVerifyBusy ? 0.6 : 1,
                        }}>
                        {phoneRemoveBusy ? 'Quitando…' : 'Quitar teléfono'}
                      </button>
                    ) : null}
                  </div>
                  {phoneFlowErr ? (
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 11,
                        color: C.danger,
                        lineHeight: 1.45,
                        background: 'rgba(226,75,74,0.08)',
                        padding: '8px 10px',
                        borderRadius: 8,
                      }}>
                      {phoneFlowErr}
                    </div>
                  ) : null}
                  {phoneFlowMsg ? (
                    <div
                      style={{
                        marginTop: 10,
                        fontSize: 11,
                        color: C.green,
                        lineHeight: 1.45,
                        background: 'rgba(29,158,117,0.08)',
                        padding: '8px 10px',
                        borderRadius: 8,
                      }}>
                      {phoneFlowMsg}
                    </div>
                  ) : null}
                </div>
                <FieldInput
                  label="Web o portafolio"
                  value={cuentaForm.website_url}
                  onChange={e => setCuentaForm(f => ({ ...f, website_url: e.target.value }))}
                  placeholder="https://…"
                />
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
                    Redes sociales (hasta 3) — elige la red y pega el enlace a tu perfil
                  </div>
                  {(Array.isArray(cuentaForm.social_links) ? cuentaForm.social_links : []).map((row, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(130px, 150px) 1fr auto',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 8,
                      }}>
                      <select
                        value={row.platform || 'instagram'}
                        onChange={e => {
                          const v = e.target.value;
                          setCuentaForm(f => {
                            const cur = [...(f.social_links || [])];
                            cur[idx] = { ...cur[idx], platform: v };
                            return { ...f, social_links: cur };
                          });
                        }}
                        style={selUbicacionBase}
                        onFocus={e => {
                          e.target.style.borderColor = C.gold;
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                        }}>
                        {SOCIAL_PLATFORMS.map(opt => (
                          <option key={opt.id} value={opt.id} style={{ background: '#1a1d28', color: '#fff' }}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <input
                        value={row.url || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setCuentaForm(f => {
                            const cur = [...(f.social_links || [])];
                            cur[idx] = { ...cur[idx], url: v };
                            return { ...f, social_links: cur };
                          });
                        }}
                        placeholder="https://…"
                        onFocus={e => {
                          e.target.style.borderColor = C.gold;
                        }}
                        onBlur={e => {
                          e.target.style.borderColor = 'rgba(255,255,255,0.12)';
                        }}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '10px 12px',
                          borderRadius: 10,
                          border: '0.5px solid rgba(255,255,255,0.12)',
                          background: 'rgba(255,255,255,0.06)',
                          color: '#fff',
                          fontSize: 13,
                          outline: 'none',
                          fontFamily: 'inherit',
                        }}
                      />
                      <button
                        type="button"
                        title="Quitar"
                        onClick={() =>
                          setCuentaForm(f => {
                            const cur = [...(f.social_links || [])];
                            cur.splice(idx, 1);
                            return { ...f, social_links: cur };
                          })
                        }
                        style={{
                          width: 36,
                          height: 36,
                          flexShrink: 0,
                          borderRadius: 8,
                          border: '0.5px solid rgba(255,255,255,0.12)',
                          background: 'rgba(255,255,255,0.04)',
                          color: 'rgba(255,255,255,0.55)',
                          cursor: 'pointer',
                          fontSize: 16,
                          lineHeight: 1,
                        }}>
                        ×
                      </button>
                    </div>
                  ))}
                  {(Array.isArray(cuentaForm.social_links) ? cuentaForm.social_links : []).length < 3 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setCuentaForm(f => {
                          const cur = Array.isArray(f.social_links) ? [...f.social_links] : [];
                          if (cur.length >= 3) return f;
                          const used = new Set(cur.map(r => r.platform));
                          const nextPl = SOCIAL_PLATFORMS.find(p => !used.has(p.id))?.id ?? 'instagram';
                          return { ...f, social_links: [...cur, { platform: nextPl, url: '' }] };
                        })
                      }
                      style={{
                        marginTop: 4,
                        padding: '8px 12px',
                        fontSize: 11,
                        borderRadius: 8,
                        border: '0.5px dashed rgba(200,168,75,0.35)',
                        background: 'transparent',
                        color: C.gold,
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: 280,
                      }}>
                      + Añadir red social
                    </button>
                  ) : null}
                </div>
              </DCard>

              <SLabel>Credenciales</SLabel>
              <DCard id="ajustes-email-change" style={{ padding: 16, marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: emailVerified ? 'rgba(29,158,117,0.08)' : 'rgba(239,159,39,0.08)', borderRadius: 10, border: `0.5px solid ${emailVerified ? 'rgba(29,158,117,0.2)' : 'rgba(239,159,39,0.2)'}`, marginBottom: 10 }}>
                  <Icon name="check" size={14} color={emailVerified ? C.green : C.warn} />
                  <span style={{ fontSize: 12, color: emailVerified ? C.green : C.warn }}>
                    {emailVerified ? 'Email verificado' : 'Email pendiente de verificación'} — {user.email}
                  </span>
                </div>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', margin: '0 0 12px', lineHeight: 1.45 }}>
                  Para usar otro correo, escríbelo abajo y solicita el cambio. Te enviaremos un enlace de confirmación al nuevo email (y a veces un aviso al actual).
                </p>
                <FieldInput
                  label="Nuevo correo electrónico"
                  type="email"
                  autoComplete="email"
                  value={emailNew}
                  onChange={e => setEmailNew(e.target.value)}
                  placeholder="nuevo@ejemplo.com"
                  disabled={emailChangeBusy}
                />
                {emailChangeErr ? (
                  <div style={{ background: 'rgba(226,75,74,0.1)', border: '0.5px solid rgba(226,75,74,0.35)', borderRadius: 10, padding: '8px 10px', fontSize: 11, color: C.danger, marginBottom: 10 }}>{emailChangeErr}</div>
                ) : null}
                {emailChangeMsg ? (
                  <div style={{ background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.35)', borderRadius: 10, padding: '8px 10px', fontSize: 11, color: C.green, marginBottom: 10 }}>{emailChangeMsg}</div>
                ) : null}
                <button
                  type="button"
                  onClick={handleRequestEmailChange}
                  disabled={emailChangeBusy}
                  style={{
                    padding: '10px 16px',
                    borderRadius: 10,
                    border: '0.5px solid rgba(200,168,75,0.45)',
                    background: 'rgba(200,168,75,0.12)',
                    color: C.gold,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: emailChangeBusy ? 'wait' : 'pointer',
                    opacity: emailChangeBusy ? 0.75 : 1,
                    fontFamily: 'inherit',
                  }}>
                  {emailChangeBusy ? 'Enviando…' : 'Solicitar cambio de correo'}
                </button>
                <div style={{ marginTop: 16, paddingTop: 12, borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
                  <FieldInput label="Nueva contraseña (opcional)" type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Mínimo 6 caracteres" autoComplete="new-password" />
                  <FieldInput label="Confirmar nueva contraseña" type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} autoComplete="new-password" />
                </div>
              </DCard>

              {cuentaErr && (
                <div style={{ background: 'rgba(226,75,74,0.1)', border: '0.5px solid rgba(226,75,74,0.35)', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.danger, marginBottom: 12 }}>{cuentaErr}</div>
              )}
              {cuentaMsg && (
                <div style={{ background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.35)', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.green, marginBottom: 12 }}>{cuentaMsg}</div>
              )}

              <BtnGold onClick={handleSaveCuenta} style={{ opacity: cuentaSaving || publicInfoSaving ? 0.7 : 1 }} disabled={cuentaSaving || publicInfoSaving}>
                {cuentaSaving ? 'Guardando…' : 'Guardar cambios'}
              </BtnGold>
            </>
          )}
        </div>
      </div>
    );
  }

  if (sub === 'privacidad') {
    const socialRows = Array.isArray(cuentaForm.social_links) ? cuentaForm.social_links : [];
    const visSlots = normalizedSocialVisibilitySlots(cuentaForm.public_social_visible);
    const rowStyle = { display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '0.5px solid rgba(255,255,255,0.06)', gap: 10 };
    const visIntro =
      'Estos interruptores guardan en tu cuenta qué ven otros usuarios en tu perfil. Teléfono, ubicación y enlaces los configuras en «Datos de la cuenta».';

    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Privacidad', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, margin: '0 0 14px' }}>{visIntro}</p>

          <SLabel>Ubicación en el perfil</SLabel>
          <DCard style={{ padding: '4px 16px', marginBottom: 14 }}>
            {[
              ['public_show_pais', 'Mostrar país', 'País o región que guardaste en tu perfil'],
              ['public_show_ciudad', 'Mostrar provincia o ciudad', 'Nivel intermedio de tu ubicación'],
              ['public_show_localidad', 'Mostrar municipio o localidad', 'Detalle fino (barrio, municipio, etc.)'],
            ].map(([key, t, s]) => (
              <div key={key} style={rowStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#fff' }}>{t}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{s}</div>
                </div>
                <Toggle
                  checked={!!cuentaForm[key]}
                  onChange={() => setCuentaForm(f => ({ ...f, [key]: !f[key] }))}
                />
              </div>
            ))}
          </DCard>

          <SLabel>Pestañas del perfil</SLabel>
          <DCard style={{ padding: '4px 16px', marginBottom: 14 }}>
            {[
              ['public_show_coleccion', 'Colección', 'Discos que tienes y no vendes'],
              ['public_show_joyas', 'Joyas', 'Discos marcados como joya en tu colección'],
              ['public_show_ofertas', 'Abiertos a ofertas', 'Discos de tu colección en los que aceptas ofertas'],
            ].map(([key, t, s]) => (
              <div key={key} style={rowStyle}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: '#fff' }}>{t}</div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{s}</div>
                </div>
                <Toggle
                  checked={!!cuentaForm[key]}
                  onChange={() => setCuentaForm(f => ({ ...f, [key]: !f[key] }))}
                />
              </div>
            ))}
          </DCard>

          <SLabel>Teléfono y redes</SLabel>
          <DCard style={{ padding: '4px 16px', marginBottom: 14 }}>
            <div style={rowStyle}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#fff' }}>Mostrar teléfono en el perfil</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                  {phoneVerified
                    ? 'Solo el número que verificaste por SMS. Puedes ocultarlo cuando quieras.'
                    : 'Verifica un número en «Datos de la cuenta» para poder mostrarlo aquí.'}
                </div>
              </div>
              <Toggle
                checked={!!cuentaForm.public_show_phone && phoneVerified}
                onChange={() => {
                  if (!phoneVerified) return;
                  setCuentaForm(f => ({ ...f, public_show_phone: !f.public_show_phone }));
                }}
              />
            </div>
            {[0, 1, 2].map(slot => {
              const row = socialRows[slot];
              const hasUrl = row && String(row.url || '').trim();
              const plLabel = row?.platform ? labelForSocialPlatform(row.platform) : null;
              const title = hasUrl ? `Mostrar ${plLabel}` : `Red social (espacio ${slot + 1})`;
              const sub = hasUrl
                ? 'Enlace definido en Editar perfil; aquí solo eliges si aparece en público.'
                : 'Sin enlace aún. Cuando añadas una URL en Editar perfil, este interruptor decidirá si se muestra.';
              return (
                <div key={`soc-vis-${slot}`} style={rowStyle}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#fff' }}>{title}</div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{sub}</div>
                  </div>
                  <Toggle
                    checked={!!visSlots[slot]}
                    onChange={() =>
                      setCuentaForm(f => {
                        const v = normalizedSocialVisibilitySlots(f.public_social_visible);
                        const next = [...v];
                        next[slot] = !next[slot];
                        return { ...f, public_social_visible: next };
                      })
                    }
                  />
                </div>
              );
            })}
            <div style={{ padding: '10px 0 4px', fontSize: 10, color: 'rgba(255,255,255,0.32)', lineHeight: 1.45 }}>
              La web que indiques en Editar perfil se muestra en el perfil si hay URL; no lleva interruptor aparte. Orden de las redes: el mismo que en Editar perfil (hasta 3).
            </div>
          </DCard>

          {privacidadErr ? (
            <div style={{ background: 'rgba(226,75,74,0.1)', border: '0.5px solid rgba(226,75,74,0.35)', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.danger, marginBottom: 12 }}>{privacidadErr}</div>
          ) : null}
          {privacidadMsg ? (
            <div style={{ background: 'rgba(29,158,117,0.1)', border: '0.5px solid rgba(29,158,117,0.35)', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: C.green, marginBottom: 12 }}>{privacidadMsg}</div>
          ) : null}

          <BtnGold onClick={handleSavePrivacidad} disabled={privacidadSaving || profileLoading} style={{ marginBottom: 22, opacity: privacidadSaving || profileLoading ? 0.75 : 1 }}>
            {privacidadSaving ? 'Guardando…' : 'Guardar visibilidad'}
          </BtnGold>

          <SLabel>Datos y RGPD</SLabel>
          <DCard style={{ padding: 16, marginBottom: 14 }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', margin: '0 0 10px', lineHeight: 1.55 }}>
              Aquí no hay interruptores sueltos: los derechos de acceso, rectificación, supresión, limitación y portabilidad dependen de la ley y de nuestra política. Si quieres ejercerlos (por ejemplo exportar o borrar datos), usa el apartado legal o escribe a soporte.
            </p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Las preferencias tipo «recibir newsletters» o «cookies / analítica» se podrán guardar aquí cuando estén enlazadas a tu cuenta; de momento no cambian nada en la base de datos, así que las hemos quitado para no confundir.
            </p>
            <button
              type="button"
              onClick={() => setSub('legal')}
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                border: '0.5px solid rgba(200,168,75,0.35)',
                background: 'rgba(200,168,75,0.1)',
                color: C.gold,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}>
              Ver términos y privacidad
            </button>
          </DCard>
        </div>
      </div>
    );
  }

  if (sub === 'pagos') {
    const walletDisplay = 'No disponible';

    const groovesRaw = profile ? profileGroovesBalance(profile) : NaN;
    const groovesDisplay =
      profileLoading ? '…' : Number.isFinite(groovesRaw) ? String(Math.max(0, Math.floor(groovesRaw))) : '0';

    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Métodos de pago', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          <SLabel>Tus saldos</SLabel>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.42)', lineHeight: 1.55, margin: '0 0 12px' }}>
            <strong style={{ color: 'rgba(255,255,255,0.72)' }}>Dinero (euros)</strong> y <strong style={{ color: 'rgba(255,255,255,0.72)' }}>Grooves</strong> son cosas distintas. El producto prevé usar el monedero en euros para pagos de mercado cuando Stripe esté listo (p. ej. pujas con reserva de importe y custodia en{' '}
            <strong style={{ color: 'rgba(255,255,255,0.72)' }}>tienda verificadora</strong>
            ); <strong style={{ color: 'rgba(255,255,255,0.72)' }}>en el código actual eso aún no cobra</strong> desde este saldo. Los Grooves ya viven en tu cuenta y se gastan en flujos que sí están enlazados (IA, comunidad, partners…).
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
            <DCard
              style={{
                flex: '1 1 260px',
                padding: 16,
                minWidth: 0,
                background: 'linear-gradient(135deg,#1a1a2e,#0d1a2e)',
                borderColor: 'rgba(200,168,75,0.2)',
              }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                Monedero · euros
              </div>
              <div style={{ fontSize: 28, fontWeight: 600, color: C.gold, lineHeight: 1.15 }}>{walletDisplay}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', marginTop: 6, lineHeight: 1.45 }}>
                Objetivo de negocio: pagos en subastas y custodia/verificación con tienda verificadora. Pendiente: backend + Stripe.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <BtnGold
                  type="button"
                  disabled
                  style={{ flex: 2, borderRadius: 9, padding: '9px', fontSize: 11, opacity: 0.45, cursor: 'not-allowed' }}>
                  Recargar
                </BtnGold>
                <button
                  type="button"
                  disabled
                  style={{
                    flex: 1,
                    padding: '9px',
                    borderRadius: 9,
                    background: 'rgba(255,255,255,0.05)',
                    border: 'none',
                    color: 'rgba(255,255,255,0.35)',
                    fontSize: 11,
                    cursor: 'not-allowed',
                  }}>
                  Retirar
                </button>
              </div>
            </DCard>

            <DCard
              style={{
                flex: '1 1 260px',
                padding: 16,
                minWidth: 0,
                background: 'linear-gradient(135deg,#15152a,#122018)',
                borderColor: 'rgba(29,158,117,0.22)',
              }}>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                Grooves
              </div>
              <div style={{ fontSize: 28, fontWeight: 600, color: C.green, lineHeight: 1.15 }}>{groovesDisplay}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', marginTop: 6, lineHeight: 1.45 }}>
                Puntos en cuenta (ledger). No son euros: sirven para escaneos IA, sorteos/comunidad, solicitudes partner, etc.
              </div>
              <button
                type="button"
                onClick={() => setSub('cuenta')}
                style={{
                  marginTop: 12,
                  width: '100%',
                  padding: '9px 10px',
                  borderRadius: 9,
                  border: '0.5px solid rgba(200,168,75,0.35)',
                  background: 'rgba(200,168,75,0.1)',
                  color: C.gold,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}>
                Comprar Grooves (Stripe) → Editar perfil
              </button>
            </DCard>
          </div>

          <SLabel>Qué falta para Stripe (monedero EUR)</SLabel>
          <DCard style={{ padding: 14, marginBottom: 14, background: 'rgba(83,74,183,0.06)', borderColor: 'rgba(83,74,183,0.18)' }}>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.48)', margin: '0 0 10px', lineHeight: 1.5 }}>
              La compra de <strong style={{ color: 'rgba(255,255,255,0.75)' }}>Grooves</strong> con tarjeta ya puede apoyarse en{' '}
              <code style={{ color: C.gold, fontSize: 10 }}>grooves_stripe_packs</code>, la función{' '}
              <code style={{ color: C.gold, fontSize: 10 }}>create-checkout-grooves</code> (Grooves) y{' '}
              <code style={{ color: C.gold, fontSize: 10 }}>create-checkout-listing</code> (compras de disco en el mercado).
              Ninguno de los dos rellena este monedero en euros; las compras de mercado generan un pedido en Mis compras.
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
              <li>Modelo en Supabase: saldo (p. ej. céntimos) y ledger de movimientos con RLS.</li>
              <li>Stripe Customer por usuario (p. ej. <code style={{ color: C.gold, fontSize: 10 }}>stripe_customer_id</code> en <code style={{ color: C.gold, fontSize: 10 }}>profiles</code> o tabla asociada).</li>
              <li>Edge Function (service role) que cree Checkout o PaymentIntent para recarga segura.</li>
              <li>Webhook Stripe (<code style={{ color: C.gold, fontSize: 10 }}>checkout.session.completed</code> / pago exitoso) que acredite saldo solo tras confirmación.</li>
              <li>Para cobrar ventas a IBAN: Stripe Connect u otro flujo de payouts; en partners existe <code style={{ color: C.gold, fontSize: 10 }}>stripe_account_id</code> como ancla futura.</li>
            </ul>
          </DCard>

          <SLabel>Métodos guardados</SLabel>
          <DCard style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
              Aún no hay tarjetas ni métodos enlazados a tu cuenta. Cuando exista el flujo Stripe (arriba), podrás añadirlos aquí.
            </div>
          </DCard>
          <button
            type="button"
            disabled
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 12,
              background: 'transparent',
              border: '1px dashed rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.28)',
              fontSize: 12,
              cursor: 'not-allowed',
              marginBottom: 14,
            }}>
            + Añadir método de pago (pendiente de Stripe)
          </button>

          <SLabel>IBAN para cobros</SLabel>
          <DCard style={{ padding: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'rgba(239,159,39,0.08)', borderRadius: 10, border: '0.5px solid rgba(239,159,39,0.2)', marginBottom: 12 }}>
              <Icon name="info" size={14} color={C.warn} />
              <span style={{ fontSize: 11, color: C.warn }}>
                Pendiente de Stripe Connect o proceso KYC acordado. El IBAN no se guardará hasta activar el flujo de cobros.
              </span>
            </div>
            <FieldInput label="IBAN" placeholder="ES00 0000 0000 0000 0000 0000" disabled />
            <FieldInput label="Titular de la cuenta" placeholder="Nombre completo" disabled />
            <BtnGold type="button" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>
              Guardar IBAN
            </BtnGold>
          </DCard>
        </div>
      </div>
    );
  }

  if (sub === 'idioma') {
    let browserLang = '—';
    let timeZone = '—';
    if (typeof navigator !== 'undefined') {
      browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || '—';
      try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '—';
      } catch (_) {
        timeZone = '—';
      }
    }

    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Idioma y región', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          <SLabel>Idioma de la app</SLabel>
          <DCard style={{ padding: 14, marginBottom: 14 }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: 0, lineHeight: 1.55 }}>
              La interfaz está <strong style={{ color: '#fff' }}>solo en español</strong>. Por ahora no puedes cambiar el idioma; cuando haya más opciones, las elegirás aquí.
            </p>
          </DCard>

          <SLabel>Región y ubicación</SLabel>
          <DCard style={{ padding: 14, marginBottom: 14 }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: 0, lineHeight: 1.55 }}>
              País, provincia y ciudad <strong style={{ color: '#fff' }}>de tu perfil público</strong> se configuran en <strong style={{ color: C.gold }}>Datos de la cuenta</strong> (no en esta pantalla).
            </p>
          </DCard>

          <SLabel>Tu navegador (referencia)</SLabel>
          <DCard style={{ padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
              <span style={{ color: 'rgba(255,255,255,0.38)' }}>Idioma preferido del navegador:</span>{' '}
              <code style={{ color: C.gold, fontSize: 11 }}>{browserLang}</code>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 10 }}>
              <span style={{ color: 'rgba(255,255,255,0.38)' }}>Zona horaria:</span>{' '}
              <code style={{ color: C.gold, fontSize: 11 }}>{timeZone}</code>
            </div>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', margin: 0, lineHeight: 1.45 }}>
              Solo en tu dispositivo; DreamVinyl no guarda estos datos en tu cuenta.
            </p>
          </DCard>

          <BtnGold type="button" onClick={() => setSub(null)}>
            Volver a Ajustes
          </BtnGold>
        </div>
      </div>
    );
  }

  if (sub === 'notif') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Notificaciones', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          {[
            { section:'Compras y ventas', items:[['pedido','Estado del pedido','Enviado, entregado, pago liberado'],['mensaje','Nuevo mensaje en chat','Cuando alguien te escribe'],['oferta','Oferta recibida','Alguien hace una oferta por tu disco'],['val','Valoración recibida','Cuando te valoran']] },
            { section:'Pujas', items:[['superado','Te han superado en una puja','Alguien puja por encima de ti'],['puja_fin','Puja finalizada','Resultado de pujas en que participaste'],['puja_cerca','Puja próxima a terminar','Aviso 1 hora antes del cierre']] },
            { section:'Lista de deseos y alertas', items:[['wishlist','Disco de tu wishlist disponible','Alguien publica un disco que buscas'],['precio','Bajada de precio','Un disco que sigues baja de precio']] },
            { section:'Comunidad y Ocio', items:[['evento','Evento cerca de ti','Nuevo evento en tu zona'],['seguidor','Nuevo seguidor','Alguien empieza a seguirte'],['grooves','Grooves y logros','Nuevos puntos y niveles desbloqueados'],['novedades','Novedades de DreamVinyl','Nuevas funciones y actualizaciones']] },
          ].map(group=>(
            <div key={group.section} style={{ marginBottom:14 }}>
              <SLabel>{group.section}</SLabel>
              <DCard style={{ padding:'4px 16px' }}>
                {group.items.map(([k,t,s])=>(
                  <div key={k} style={{ display:'flex', alignItems:'center', padding:'12px 0', borderBottom:'0.5px solid rgba(255,255,255,0.06)', gap:10 }}>
                    <div style={{ flex:1 }}><div style={{ fontSize:13, color:'#fff' }}>{t}</div><div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginTop:2 }}>{s}</div></div>
                    <Toggle checked={notifToggles[k]} onChange={()=>toggleNotif(k)}/>
                  </div>
                ))}
              </DCard>
            </div>
          ))}
          {!user?.id ? (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 12 }}>Inicia sesión para guardar tus preferencias en la cuenta.</p>
          ) : (
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 12, lineHeight: 1.45 }}>
              Los cambios se guardan automáticamente en tu perfil.
              {notifSaving ? ' Guardando…' : ''}
            </p>
          )}
          {notifErr ? <div style={{ fontSize: 11, color: C.warn, marginTop: 8 }}>{notifErr}</div> : null}
          {notifMsg && !notifSaving ? <div style={{ fontSize: 11, color: C.green, marginTop: 6 }}>{notifMsg}</div> : null}
        </div>
      </div>
    );
  }

  if (sub === 'legal') {
    return (
      <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
        {topBar('Legal', () => setSub(null))}
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
          <SLabel>Documentos legales</SLabel>
          <DCard style={{ padding:'4px 16px', marginBottom:14 }}>
            {['Términos de uso','Política de privacidad','Política de cookies','Términos para partners y negocios','Condiciones de las pujas','Política de resolución de disputas','Política de entregas físicas'].map(t=>(
              <div key={t} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 0', borderBottom:'0.5px solid rgba(255,255,255,0.06)', cursor:'pointer' }}>
                <span style={{ fontSize:13, color:'rgba(255,255,255,0.7)' }}>{t}</span>
                <span style={{ color:'rgba(255,255,255,0.25)', fontSize:16 }}>›</span>
              </div>
            ))}
          </DCard>
          <SLabel>Tus derechos RGPD</SLabel>
          <DCard style={{ padding:12, marginBottom:10, background:'rgba(83,74,183,0.06)', borderColor:'rgba(83,74,183,0.2)' }}>
            <p style={{ fontSize:10, color:C.purpleL, lineHeight:1.5 }}>Como usuario europeo tienes derecho a acceder, rectificar, suprimir y portar tus datos personales. DreamVinyl cumple íntegramente con el RGPD de la UE.</p>
          </DCard>
          <DCard style={{ padding:'4px 16px' }}>
            {['Solicitar copia de mis datos','Solicitar eliminación de datos','Contacto con el delegado de protección de datos'].map(t=>(
              <div key={t} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'13px 0', borderBottom:'0.5px solid rgba(255,255,255,0.06)', cursor:'pointer' }}>
                <span style={{ fontSize:13, color:'rgba(255,255,255,0.7)' }}>{t}</span>
                <span style={{ color:'rgba(255,255,255,0.25)', fontSize:16 }}>›</span>
              </div>
            ))}
          </DCard>
          <p style={{ fontSize:10, color:'rgba(255,255,255,0.2)', textAlign:'center', marginTop:20, lineHeight:1.6 }}>DreamVinyl v1.0.0 · © 2026 DreamVinyl<br/>Todos los derechos reservados</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: C.dark, minHeight: '100vh', color: '#fff' }}>
      <WebTopBar onBack={onBack} title="Ajustes" mini dark user={user} onNavigate={onNavigate} onSignOut={onTopBarSignedOut} />
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px 60px' }}>
        <DCard style={{ padding: '14px 16px', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: '#1a1a2e',
                border: `2px solid ${C.gold}`,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 500,
                color: C.gold,
                flexShrink: 0,
              }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : user ? (
                (displayName[0] || user.email?.[0] || '?').toUpperCase()
              ) : (
                'TU'
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>{profileLoading ? 'Cargando…' : displayName}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                {user?.email || '—'}
                {profile?.username ? ` · @${profile.username}` : ''}
                {phoneVerified ? ` · ${String(profile.phone).trim()}` : ''}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>
                {profile ? `${profileLevelLabel(profile.level)} · Nv.${profile.level}` : ''}
              </div>
              <button type="button" onClick={() => setSub('cuenta')} style={{ fontSize: 11, color: C.gold, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 4 }}>
                Editar perfil →
              </button>
            </div>
          </div>
        </DCard>

        <SLabel>Cuenta</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="user" iconBg="rgba(83,74,183,0.15)" iconColor={C.purpleL} title="Datos de la cuenta" sub="Foto, usuario, bio, teléfono, correo, redes, contraseña" onClick={()=>setSub('cuenta')}/>
          <SettingsRow
            icon="mail"
            iconBg="rgba(29,158,117,0.15)"
            iconColor={C.green}
            title="Correo electrónico"
            sub={emailVerified ? 'Ver tu correo, verificación y solicitar un cambio' : 'Confirma el registro o cambia de correo desde Editar perfil'}
            right={<Badge type={emailVerified ? 'green' : 'warn'}>{emailVerified ? 'Email verificado' : 'Pendiente'}</Badge>}
            onClick={() => {
              scrollEmailSectionRef.current = true;
              setSub('cuenta');
            }}
          />
        </DCard>

        <SLabel>Privacidad</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="lock" iconBg="rgba(55,138,221,0.15)" iconColor="#85B7EB" title="Privacidad y visibilidad" sub="Ubicación, pestañas del perfil, teléfono y redes" onClick={()=>setSub('privacidad')}/>
        </DCard>

        <SLabel>Pagos</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="card" iconBg="rgba(200,168,75,0.15)" iconColor={C.gold} title="Métodos de pago" sub="Saldo € y Grooves; Stripe EUR pendiente" onClick={()=>setSub('pagos')}/>
          <SettingsRow icon="dollar" iconBg="rgba(200,168,75,0.15)" iconColor={C.gold} title="Datos bancarios para cobros" sub="IBAN para recibir tus ventas" right={<Badge type="warn">Pendiente</Badge>} onClick={()=>setSub('pagos')}/>
        </DCard>

        <SLabel>Preferencias</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="bell" iconBg="rgba(216,90,48,0.15)" iconColor="#F0997B" title="Notificaciones" sub="Qué avisos quieres recibir" onClick={()=>setSub('notif')}/>
          <SettingsRow
            icon="globe"
            iconBg="rgba(255,255,255,0.06)"
            iconColor="rgba(255,255,255,0.5)"
            title="Idioma y región"
            sub="Solo español por ahora · referencia de tu navegador"
            onClick={() => setSub('idioma')}
          />
        </DCard>

        <SLabel>Soporte</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="help" iconBg="rgba(255,255,255,0.06)" iconColor="rgba(255,255,255,0.5)" title="Centro de ayuda" sub="Preguntas frecuentes y guías" onClick={() => onNavigate('centro-ayuda')} />
          <SettingsRow icon="chat" iconBg="rgba(255,255,255,0.06)" iconColor="rgba(255,255,255,0.5)" title="Contactar soporte" sub="Resolvemos tu consulta lo antes posible" onClick={openSupportEmail}/>
        </DCard>

        <SLabel>Legal</SLabel>
        <DCard style={{ padding:'4px 16px', marginBottom:12 }}>
          <SettingsRow icon="file" iconBg="rgba(255,255,255,0.06)" iconColor="rgba(255,255,255,0.5)" title="Términos y privacidad" sub="Política de privacidad, RGPD, términos" onClick={()=>setSub('legal')}/>
        </DCard>

        <DCard style={{ padding:14, marginBottom:10, background:'rgba(226,75,74,0.06)', borderColor:'rgba(226,75,74,0.15)' }}>
          <div style={{ fontSize:11, color:C.danger, fontWeight:500, marginBottom:10, textTransform:'uppercase', letterSpacing:'0.5px' }}>Zona de peligro</div>
          <button type="button" style={{ width:'100%', padding:11, borderRadius:10, background:'transparent', border:`0.5px solid rgba(226,75,74,0.4)`, color:C.danger, fontSize:13, cursor:'pointer', marginBottom:8 }}>Exportar mis datos — RGPD</button>
          <button type="button" style={{ width:'100%', padding:11, borderRadius:10, background:'transparent', border:`0.5px solid rgba(226,75,74,0.4)`, color:C.danger, fontSize:13, cursor:'pointer' }}>Eliminar cuenta permanentemente</button>
        </DCard>

        <DCard style={{ padding:14, marginBottom:12, background:'rgba(255,255,255,0.03)', borderColor:'rgba(255,255,255,0.08)' }}>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginBottom:10, lineHeight:1.45 }}>
            Si la web deja de cargar datos, restablece la sesión local (no borra favoritos ni el resto del navegador).
          </div>
          <button type="button" onClick={resetLocalSession} style={{ width:'100%', padding:12, borderRadius:12, background:'rgba(200,168,75,0.12)', border:'0.5px solid rgba(200,168,75,0.35)', color:'#C8A84B', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Restablecer sesión
          </button>
        </DCard>

        <button type="button" onClick={signOutFromAccountPanel} style={{ width:'100%', padding:12, borderRadius:12, background:'rgba(255,255,255,0.05)', border:`0.5px solid rgba(255,255,255,0.12)`, color:'rgba(255,255,255,0.6)', fontSize:13, cursor:'pointer', marginBottom:20 }}>
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}
